import { useEditorT } from '@i18n/editor';
import { isRealTap, recordTapStart } from '@/lib/tapGuard';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Heart, LayoutTemplate } from 'lucide-react';
import { editorUrl } from '@/lib/editorBasePath';
import { ApiTemplateObject, LayoutTemplateDef, parseTemplateToLayoutDef } from '@/lib/parseTemplateObjects';
import { TrameTemplateInfosByStyle } from '@/hooks/useBookLoader';
import TemplatePreview from './TemplatePreview';

export type { SlotDef, TextTemplateDef, LayoutTemplateDef } from '@/lib/parseTemplateObjects';

interface ApiTemplate {
  id: number;
  count_photo: number;
  objects_position: ApiTemplateObject[];
  background_image_url?: string;
  trame_background_id?: number;
  is_favoris?: boolean;
}

type ApiResponseItem = ApiTemplate | [ApiTemplate, ApiTemplate];
type ApiResponse = Record<string, ApiResponseItem[]>;

interface LayoutPanelProps {
  onApplyLayout: (layout: LayoutTemplateDef, templateId?: number) => void;
  activeTemplateId?: number | null;
  trameTemplateInfos: TrameTemplateInfosByStyle;
  isWememberz?: boolean;
}

type TemplateStyle = 'creatifs' | 'classiques';

const ENDPOINTS: Record<TemplateStyle, string> = {
  creatifs: editorUrl('/load_trame_templates_creatifs'),
  classiques: editorUrl('/load_trame_templates'),
};

interface ParsedTemplate {
  id: number;
  countPhoto: number;
  def: LayoutTemplateDef;
  secondDef?: LayoutTemplateDef;
}

const TemplateCard: React.FC<{
  tpl: ParsedTemplate;
  isActive: boolean;
  isFav: boolean;
  showTextContent: boolean;
  onApply: (def: LayoutTemplateDef, id?: number) => void;
  onToggleFav: (e: React.MouseEvent) => void;
}> = ({ tpl, isActive, isFav, showTextContent, onApply, onToggleFav }) => {
  const { te } = useEditorT();
  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>, def: LayoutTemplateDef) => {
    if (e.pointerType === 'mouse') return;
    if (!isRealTap(e)) return; // le doigt a glissé : c'était un scroll, pas un appui
    e.preventDefault();
    e.stopPropagation();
    onApply(def, tpl.id);
  };

  return (
  <div className="relative group">
    <div
      className={`w-full rounded-md border transition-colors p-2 ${
        isActive
          ? 'border-editor-sidebar-active bg-editor-sidebar-active/10 ring-1 ring-editor-sidebar-active'
          : 'border-editor-sidebar-border bg-editor-sidebar-hover hover:border-editor-sidebar-active'
      }`}
    >
      {tpl.secondDef ? (
        <div className="flex w-full min-w-0 gap-0.5 overflow-hidden">
          <button className="w-1/2 min-w-0 flex-none overflow-hidden align-top touch-manipulation" onPointerDown={recordTapStart} onPointerUp={(e) => handlePointerUp(e, tpl.def)} onClick={() => onApply(tpl.def, tpl.id)}>
            <TemplatePreview def={tpl.def} showTextContent={showTextContent} />
          </button>
          <button className="w-1/2 min-w-0 flex-none overflow-hidden align-top touch-manipulation" onPointerDown={recordTapStart} onPointerUp={(e) => handlePointerUp(e, tpl.secondDef!)} onClick={() => onApply(tpl.secondDef!, tpl.id)}>
            <TemplatePreview def={tpl.secondDef} showTextContent={showTextContent} />
          </button>
        </div>
      ) : (
        <button className="w-full touch-manipulation" onPointerDown={recordTapStart} onPointerUp={(e) => handlePointerUp(e, tpl.def)} onClick={() => onApply(tpl.def, tpl.id)}>
          <TemplatePreview def={tpl.def} showTextContent={showTextContent} />
        </button>
      )}
    </div>
    <button
      onClick={onToggleFav}
      className="absolute top-3 right-3 p-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
      title={isFav ? te("Retirer des favoris") : te("Ajouter aux favoris")}
    >
      <Heart size={14} className={isFav ? 'text-red-500 fill-red-500' : 'text-white'} />
    </button>
  </div>
  );
};

const LayoutPanel: React.FC<LayoutPanelProps> = ({ onApplyLayout, activeTemplateId, trameTemplateInfos, isWememberz = false }) => {
  const { te } = useEditorT();
  const [style, setStyle] = useState<TemplateStyle>(isWememberz ? 'classiques' : 'creatifs');
  const [templates, setTemplates] = useState<Record<string, ParsedTemplate[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());
  const initializedStyleRef = useRef<TemplateStyle | null>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollToCategory = useCallback((key: string) => {
    categoryRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTemplates(null);

    fetch(ENDPOINTS[style])
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        if (cancelled) return;
        const grouped: Record<string, ParsedTemplate[]> = {};
        const favSet = new Set<number>();

        const parseSingle = (tpl: ApiTemplate): ParsedTemplate => ({
          id: tpl.id,
          countPhoto: tpl.count_photo,
          def: {
            ...parseTemplateToLayoutDef(tpl.objects_position),
            backgroundImageUrl: tpl.background_image_url,
            trameBackgroundId: tpl.trame_background_id,
          },
        });

        for (const [key, tpls] of Object.entries(data)) {
          grouped[key] = tpls.map((item) => {
            if (Array.isArray(item)) {
              const first = parseSingle(item[0]);
              const second = parseSingle(item[1]);
              if (item[0].is_favoris) favSet.add(first.id);
              return { ...first, secondDef: second.def };
            }
            if (item.is_favoris) favSet.add(item.id);
            return parseSingle(item);
          });
        }
        // If creatifs loaded empty, auto-switch to classiques
        if (style === 'creatifs' && Object.keys(grouped).length === 0) {
          setStyle('classiques');
          return;
        }
        setTemplates(grouped);
        setFavorites(favSet);
        if (style === 'creatifs' && initializedStyleRef.current !== 'creatifs') {
          const sortedByName = Object.keys(grouped).sort((a, b) => {
            const nameA = trameTemplateInfos[a]?.name ?? a;
            const nameB = trameTemplateInfos[b]?.name ?? b;
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
          });
          // Keep favoris + last category (alphabetically) visible, hide the rest
          const hidden = new Set<string>(sortedByName.slice(0, -1));
          setHiddenCategories(hidden);
          initializedStyleRef.current = 'creatifs';
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [style]);

  const [customTemplates, setCustomTemplates] = useState<ParsedTemplate[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetch(editorUrl('/load_custom_templates'))
        .then((res) => res.json())
        .then((data: ApiTemplate[] | Record<string, ApiTemplate[]> | { templates?: ApiTemplate[] }) => {
          if (cancelled) return;
          const list: ApiTemplate[] = Array.isArray(data)
            ? data
            : Array.isArray((data as { templates?: ApiTemplate[] }).templates)
              ? (data as { templates: ApiTemplate[] }).templates
              : Object.values(data as Record<string, ApiTemplate[]>).flat();
          setCustomTemplates(list.filter((t) => t && Array.isArray(t.objects_position)).map((t) => ({
            id: t.id,
            countPhoto: t.count_photo,
            def: {
              ...parseTemplateToLayoutDef(t.objects_position),
              backgroundImageUrl: t.background_image_url ?? t.objects_position.find((o) => o.object_type === 'background')?.image_url,
              trameBackgroundId: t.trame_background_id ?? t.objects_position.find((o) => o.object_type === 'background')?.trame_background_id,
            },
          })));
        })
        .catch(() => { if (!cancelled) setCustomTemplates([]); });
    };
    load();
    window.addEventListener('yearbook:custom-templates-changed', load);
    return () => { cancelled = true; window.removeEventListener('yearbook:custom-templates-changed', load); };
  }, []);

  const toggleFavorite = useCallback((e: React.MouseEvent, templateId: number) => {
    e.stopPropagation();
    const isFav = favorites.has(templateId);
    const endpoint = isFav ? editorUrl('/remove_trame_template_to_favori') : editorUrl('/add_trame_template_to_favori');

    setFavorites((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(templateId);
      else next.add(templateId);
      return next;
    });

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ trame_template_id: templateId }),
    }).catch(() => {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(templateId);
        else next.delete(templateId);
        return next;
      });
    });
  }, [favorites]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-muted-foreground" size={20} />
      </div>
    );
  }

  if (!templates || Object.keys(templates).length === 0) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-editor-sidebar-foreground">{te("Modèles")}</h3>
        <p className="text-xs text-muted-foreground">{te("Aucun modèle disponible.")}</p>
      </div>
    );
  }

  const sortedKeys = Object.keys(templates).sort((a, b) => {
    if (style === 'creatifs') {
      const nameA = trameTemplateInfos[a]?.name ?? a;
      const nameB = trameTemplateInfos[b]?.name ?? b;
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
    }
    return Number(a) - Number(b);
  });
  const showTextContent = style === 'creatifs';

  const toggleCategory = (key: string) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <h3 className="text-sm font-semibold text-editor-sidebar-foreground text-center flex items-center justify-center gap-1.5"><LayoutTemplate size={15} />{te("Modèles")}</h3>

      {/* Style toggle - hidden for wememberz */}
      {!isWememberz && (
        <div className="flex rounded-lg bg-editor-sidebar-hover p-0.5 gap-0.5">
          <button
            onClick={() => setStyle('creatifs')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
              style === 'creatifs'
                ? 'bg-editor-sidebar-active text-white shadow-sm'
                : 'text-editor-sidebar-foreground hover:text-editor-sidebar-active'
            }`}
          >{te("Créatifs")}</button>
          <button
            onClick={() => setStyle('classiques')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
              style === 'classiques'
                ? 'bg-editor-sidebar-active text-white shadow-sm'
                : 'text-editor-sidebar-foreground hover:text-editor-sidebar-active'
            }`}
          >{te("Classiques")}</button>
        </div>
      )}

      {/* Favoris toggle for créatifs */}
      {style === 'creatifs' && hiddenCategories.has('__favoris__') && (
        <button
          onClick={() => toggleCategory('__favoris__')}
          className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-dashed border-editor-sidebar-border bg-editor-sidebar-hover/40 text-xs font-medium text-muted-foreground hover:text-editor-sidebar-active hover:border-editor-sidebar-active transition-colors"
        >
          <span>{te("❤️ Favoris")}</span>
          <span className="text-[10px] opacity-70">{te("Afficher")}</span>
        </button>
      )}

      {/* Quick nav for classiques */}
      {style === 'classiques' && sortedKeys.length > 3 && (
        <div className="flex flex-wrap gap-1">
          {sortedKeys.map((key) => (
            <button
              key={key}
              onClick={() => scrollToCategory(key)}
              className="text-[10px] px-1.5 py-0.5 rounded border border-editor-sidebar-border bg-editor-sidebar-hover text-editor-sidebar-foreground hover:border-editor-sidebar-active hover:text-editor-sidebar-active transition-colors"
            >
              {key}
            </button>
          ))}
        </div>
      )}

      {/* Mes modèles (créatifs) */}
      {style === 'creatifs' && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground">{te("📁 Mes modèles")}</h4>
          {customTemplates === null ? (
            <Loader2 className="animate-spin text-muted-foreground" size={14} />
          ) : customTemplates.length === 0 ? (
            <p className="text-[10px] text-muted-foreground italic">{te("Aucun modèle personnel. Créez-en un depuis le menu « ... » d'une page.")}</p>
          ) : (
            <div className="flex flex-col gap-2">
              {customTemplates.map((tpl) => (
                <TemplateCard
                  key={`custom-${tpl.id}`}
                  tpl={tpl}
                  isActive={activeTemplateId === tpl.id}
                  isFav={favorites.has(tpl.id)}
                  showTextContent={true}
                  onApply={(def, id) => onApplyLayout(def, id)}
                  onToggleFav={(e) => toggleFavorite(e, tpl.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Favoris section */}
      {!hiddenCategories.has('__favoris__') && (() => {
        const favTemplates = sortedKeys.flatMap((key) => (templates[key] || []).filter((tpl) => favorites.has(tpl.id)));
        if (favTemplates.length === 0) return (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground">{te("❤️ Favoris")}</h4>
            <p className="text-[10px] text-muted-foreground italic">{te("Aucun favori pour le moment.")}</p>
          </div>
        );
        return (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground">{te("❤️ Favoris")}</h4>
            <div className={showTextContent ? 'flex flex-col gap-2' : 'grid grid-cols-2 gap-2'}>
              {favTemplates.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  tpl={tpl}
                  isActive={activeTemplateId === tpl.id}
                  isFav={true}
                  showTextContent={showTextContent}
                  onApply={(def, id) => onApplyLayout(def, id)}
                  onToggleFav={(e) => toggleFavorite(e, tpl.id)}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {sortedKeys.map((key) => {
        const isHidden = style === 'creatifs' && hiddenCategories.has(key);
        const count = templates[key]?.length ?? 0;

        const categoryName = style === 'creatifs' && trameTemplateInfos[key]
          ? trameTemplateInfos[key].name
          : `${key} photo${Number(key) > 1 ? 's' : ''}`;

        if (isHidden) {
          return (
            <button
              key={key}
              ref={(el) => { categoryRefs.current[key] = el as unknown as HTMLDivElement; }}
              onClick={() => toggleCategory(key)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-dashed border-editor-sidebar-border bg-editor-sidebar-hover/40 text-xs font-medium text-muted-foreground hover:text-editor-sidebar-active hover:border-editor-sidebar-active transition-colors"
            >
              <span>{categoryName} <span className="opacity-60">({count})</span></span>
              <span className="text-[10px] opacity-70">{te("Afficher")}</span>
            </button>
          );
        }

        const canCollapse = style === 'creatifs';
        return (
          <div key={key} className="space-y-2" ref={(el) => { categoryRefs.current[key] = el; }}>
            {canCollapse ? (
              <button
                onClick={() => toggleCategory(key)}
                className="w-full flex items-center justify-between text-xs font-medium text-muted-foreground hover:text-editor-sidebar-active transition-colors"
              >
                <span>{categoryName} <span className="opacity-60">({count})</span></span>
                <span className="text-[10px] opacity-70">{te("Masquer")}</span>
              </button>
            ) : (
              <h4 className="text-xs font-medium text-muted-foreground">
                {categoryName}
              </h4>
            )}
            <div className={showTextContent ? 'flex flex-col gap-2' : 'grid grid-cols-2 gap-2'}>
              {templates[key].map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  tpl={tpl}
                  isActive={activeTemplateId === tpl.id}
                  isFav={favorites.has(tpl.id)}
                  showTextContent={showTextContent}
                  onApply={(def, id) => onApplyLayout(def, id)}
                  onToggleFav={(e) => toggleFavorite(e, tpl.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LayoutPanel;